"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var roosterjs_editor_dom_1 = require("roosterjs-editor-dom");
/**
 * @internal
 * DOMEventPlugin handles customized DOM events, including:
 * 1. Keyboard event
 * 2. Mouse event
 * 3. IME state
 * 4. Drop event
 * 5. Focus and blur event
 * 6. Input event
 * 7. Scroll event
 * It contains special handling for Safari since Safari cannot get correct selection when onBlur event is triggered in editor.
 */
var DOMEventPlugin = /** @class */ (function () {
    /**
     * Construct a new instance of DOMEventPlugin
     * @param options The editor options
     * @param contentDiv The editor content DIV
     */
    function DOMEventPlugin(options, contentDiv) {
        var _this = this;
        var _a;
        this.onDrop = function (e) {
            _this.editor.runAsync(function (editor) {
                editor.addUndoSnapshot(function () { }, "Drop" /* Drop */);
            });
        };
        this.onFocus = function () {
            _this.editor.select(_this.state.selectionRange);
            _this.state.selectionRange = null;
        };
        this.onKeyDownDocument = function (event) {
            if (event.which == 9 /* TAB */ && !event.defaultPrevented) {
                _this.cacheSelection();
            }
        };
        this.onMouseDownDocument = function (event) {
            if (!_this.state.selectionRange && !_this.editor.contains(event.target)) {
                _this.cacheSelection();
            }
        };
        this.cacheSelection = function () {
            if (!_this.state.selectionRange) {
                _this.state.selectionRange = _this.editor.getSelectionRange(false /*tryGetFromCache*/);
            }
        };
        this.onScroll = function (e) {
            _this.editor.triggerPluginEvent(14 /* Scroll */, {
                rawEvent: e,
                scrollContainer: _this.state.scrollContainer,
            });
        };
        this.onKeyboardEvent = function (event) {
            if (roosterjs_editor_dom_1.isCharacterValue(event)) {
                event.stopPropagation();
            }
        };
        this.onInputEvent = function (event) {
            event.stopPropagation();
        };
        this.onContextMenuEvent = function (event) {
            var allItems = [];
            var searcher = _this.editor.getContentSearcherOfCursor();
            var elementBeforeCursor = searcher === null || searcher === void 0 ? void 0 : searcher.getInlineElementBefore();
            var eventTargetNode = event.target;
            if (event.button != 2) {
                eventTargetNode = elementBeforeCursor === null || elementBeforeCursor === void 0 ? void 0 : elementBeforeCursor.getContainerNode();
            }
            _this.state.contextMenuProviders.forEach(function (provider) {
                var items = provider.getContextMenuItems(eventTargetNode);
                if ((items === null || items === void 0 ? void 0 : items.length) > 0) {
                    if (allItems.length > 0) {
                        allItems.push(null);
                    }
                    roosterjs_editor_dom_1.arrayPush(allItems, items);
                }
            });
            _this.editor.triggerPluginEvent(16 /* ContextMenu */, {
                rawEvent: event,
                items: allItems,
            });
        };
        this.state = {
            isInIME: false,
            scrollContainer: options.scrollContainer || contentDiv,
            selectionRange: null,
            stopPrintableKeyboardEventPropagation: !options.allowKeyboardEventPropagation,
            contextMenuProviders: ((_a = options.plugins) === null || _a === void 0 ? void 0 : _a.filter(isContextMenuProvider)) || [],
        };
    }
    /**
     * Get a friendly name of  this plugin
     */
    DOMEventPlugin.prototype.getName = function () {
        return 'DOMEvent';
    };
    /**
     * Initialize this plugin. This should only be called from Editor
     * @param editor Editor instance
     */
    DOMEventPlugin.prototype.initialize = function (editor) {
        var _a;
        var _this = this;
        var _b, _c, _d;
        this.editor = editor;
        var document = this.editor.getDocument();
        var eventHandlers = (_a = {
                // 1. Keyboard event
                keypress: this.getEventHandler(1 /* KeyPress */),
                keydown: this.getEventHandler(0 /* KeyDown */),
                keyup: this.getEventHandler(2 /* KeyUp */),
                // 2. Mouse event
                mousedown: 5 /* MouseDown */,
                contextmenu: this.onContextMenuEvent,
                // 3. IME state management
                compositionstart: function () { return (_this.state.isInIME = true); },
                compositionend: function (rawEvent) {
                    _this.state.isInIME = false;
                    editor.triggerPluginEvent(4 /* CompositionEnd */, {
                        rawEvent: rawEvent,
                    });
                },
                // 4. Drop event
                drop: this.onDrop,
                // 5. Focus management
                focus: this.onFocus
            },
            // 6. Input event
            _a[roosterjs_editor_dom_1.Browser.isIE ? 'textinput' : 'input'] = this.getEventHandler(3 /* Input */),
            _a);
        // 7. onBlur handlers
        if (roosterjs_editor_dom_1.Browser.isSafari) {
            document.addEventListener('mousedown', this.onMouseDownDocument, true /*useCapture*/);
            document.addEventListener('keydown', this.onKeyDownDocument);
            (_b = document.defaultView) === null || _b === void 0 ? void 0 : _b.addEventListener('blur', this.cacheSelection);
        }
        else {
            eventHandlers[roosterjs_editor_dom_1.Browser.isIEOrEdge ? 'beforedeactivate' : 'blur'] = this.cacheSelection;
        }
        this.disposer = editor.addDomEventHandler(eventHandlers);
        // 8. Scroll event
        this.state.scrollContainer.addEventListener('scroll', this.onScroll);
        (_c = document.defaultView) === null || _c === void 0 ? void 0 : _c.addEventListener('scroll', this.onScroll);
        (_d = document.defaultView) === null || _d === void 0 ? void 0 : _d.addEventListener('resize', this.onScroll);
    };
    /**
     * Dispose this plugin
     */
    DOMEventPlugin.prototype.dispose = function () {
        var _a, _b, _c;
        var document = this.editor.getDocument();
        if (roosterjs_editor_dom_1.Browser.isSafari) {
            document.removeEventListener('mousedown', this.onMouseDownDocument, true /*useCapture*/);
            document.removeEventListener('keydown', this.onKeyDownDocument);
            (_a = document.defaultView) === null || _a === void 0 ? void 0 : _a.removeEventListener('blur', this.cacheSelection);
        }
        (_b = document.defaultView) === null || _b === void 0 ? void 0 : _b.removeEventListener('resize', this.onScroll);
        (_c = document.defaultView) === null || _c === void 0 ? void 0 : _c.removeEventListener('scroll', this.onScroll);
        this.state.scrollContainer.removeEventListener('scroll', this.onScroll);
        this.disposer();
        this.disposer = null;
        this.editor = null;
    };
    /**
     * Get plugin state object
     */
    DOMEventPlugin.prototype.getState = function () {
        return this.state;
    };
    DOMEventPlugin.prototype.getEventHandler = function (eventType) {
        return this.state.stopPrintableKeyboardEventPropagation
            ? {
                pluginEventType: eventType,
                beforeDispatch: eventType == 3 /* Input */ ? this.onInputEvent : this.onKeyboardEvent,
            }
            : eventType;
    };
    return DOMEventPlugin;
}());
exports.default = DOMEventPlugin;
function isContextMenuProvider(source) {
    var _a;
    return !!((_a = source) === null || _a === void 0 ? void 0 : _a.getContextMenuItems);
}
//# sourceMappingURL=DOMEventPlugin.js.map